import { PersonChangesInfo } from '@aibees/elephant/feature/search-table-people';
import { HttpMethod } from '@aibees/shared/definition';
import { useCurrentValue } from '@aibees/shared/hooks';
import { useBaseQuery } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback } from 'react';
import { COMPANY_READ_API_CHANGES } from '../../constants';
import { ReadPersonRequest } from '../types';

let globalDebounceTimeout: NodeJS.Timeout | null = null;

export function useReadCompanyChanges(id?: string) {
  const workspaceId = useWorkspace()?.id;

  // Track the latest id so the deferred refetch can re-check it at fire time.
  // The schedule-time `if (!id) return` only blocks new timers; without this
  // check a timer scheduled while id was valid can still fire after id has
  // flipped to undefined and refetch the wrong queryKey.
  const idRef = useCurrentValue(id);

  const api = useBaseQuery<ReadPersonRequest, PersonChangesInfo>(
    [useReadCompanyChanges.name, id],
    {
      url: COMPANY_READ_API_CHANGES.replace(
        ':wid',
        String(workspaceId)
      ).replace(':id', String(id)),
      method: HttpMethod.get,
      headers: { 'x-customer-id': workspaceId }, // todo: ask sayyad if this is needed here
    },
    {
      enabled: false,
    }
  );

  const debouncedRefetch = useCallback(() => {
    if (!id) return;
    if (globalDebounceTimeout) {
      clearTimeout(globalDebounceTimeout);
    }
    globalDebounceTimeout = setTimeout(() => {
      if (!idRef.current) return;
      api.refetch();
      globalDebounceTimeout = null;
    }, 1000); // Adjust the delay as needed.
  }, [api, id, idRef]);

  return { ...api, debouncedRefetch };
}
